/**
 * ============================================================
 * System       : CSAF Alert Finance
 * File         : frontend/src/pages/Reconciliation.jsx
 * Description  : SAP vs Bank Statement reconciliation tool — matches
 *                records by date + amount, entirely client-side.
 * ============================================================
 */

import React, { useState, useCallback, useMemo } from "react";
// Deep-import the exact CJS file (xlsx/xlsx.js), not the bare "xlsx"
// package. Confirmed by inspecting node_modules directly: xlsx.js (the CJS
// build, package.json's "main") correctly does `XLSX.utils = utils` etc. on
// a plain object with nothing bundler-sensitive about it. xlsx.mjs (the ESM
// build, package.json's "module") is broken under CRA's webpack 5 — .utils
// comes back undefined at runtime. webpack's resolver applies the same
// browser/module/main field priority to `require()` as it does to `import`
// (unlike plain Node.js), so a bare `require("xlsx")` still resolved to the
// broken xlsx.mjs — this path bypasses that field-based resolution entirely
// by naming the working file directly.
const { read: readXLSX, utils: xlsxUtils, writeFile: writeXLSXFile, SSF } = require("xlsx/xlsx.js");
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Download,
  Loader2,
  ScanLine,
  RotateCcw,
  Save,
} from "lucide-react";
import { saveReconciliation } from "../api";
import { useToast } from "../App";

// ============================================================
// CONFIG — ตำแหน่งคอลัมน์ในไฟล์ต้นฉบับ (นับจาก 0)
// ============================================================
const SAP_FIRST_DATA_ROW = 5;
const SAP_COL_DATE = 8;
const SAP_COL_AMOUNT = 10;
const SAP_COL_DOCNO = 5;
const SAP_COL_TEXT = 14;

const BANK_FIRST_DATA_ROW = 3;
const BANK_COL_DATE = 0;
const BANK_COL_DEBIT = 4;
const BANK_COL_CREDIT = 5;
const BANK_COL_CHANNEL = 9;
const BANK_COL_DETAIL = 10;

const TOLERANCE_DAYS = 3;

// ============================================================
// Helpers
// ============================================================
const pad2 = (n) => String(n).padStart(2, "0");

function toNum(v) {
  if (v === null || v === undefined) return NaN;
  if (typeof v === "number") return v;
  const s = String(v).trim();
  if (s === "") return NaN;
  const n = parseFloat(s.replace(/,/g, ""));
  return n;
}

// Returns { day, month, dateObj (year 2000 fixed), label }
function parseAnyDate(value) {
  if (value === null || value === undefined) return null;

  if (value instanceof Date && !isNaN(value)) {
    const month = value.getMonth() + 1;
    const day = value.getDate();
    return { day, month, dateObj: new Date(2000, month - 1, day) };
  }

  const s = String(value).trim();
  let m = s.match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{2,4})$/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    return { day, month, dateObj: new Date(2000, month - 1, day) };
  }

  if (typeof value === "number") {
    const parsed = SSF.parse_date_code(value);
    if (parsed) {
      return {
        day: parsed.d,
        month: parsed.m,
        dateObj: new Date(2000, parsed.m - 1, parsed.d),
      };
    }
  }
  return null;
}

export function round2(n) {
  return Math.round(n * 100) / 100;
}

// ============================================================
// Parsers
// ============================================================
function parseSapSheet(aoa) {
  const rows = [];
  for (let i = SAP_FIRST_DATA_ROW; i < aoa.length; i++) {
    const row = aoa[i];
    if (!row) continue;
    const dateRaw = row[SAP_COL_DATE];
    const amtRaw = row[SAP_COL_AMOUNT];
    if (dateRaw === null || dateRaw === undefined) continue;
    if (amtRaw === null || amtRaw === undefined) continue;
    const d = parseAnyDate(dateRaw);
    const amt = toNum(amtRaw);
    if (!d || isNaN(amt)) continue;
    rows.push({
      day: d.day,
      month: d.month,
      dateObj: d.dateObj,
      dateLabel: `${pad2(d.day)}/${pad2(d.month)}`,
      amount: round2(amt),
      docno: row[SAP_COL_DOCNO] ?? "",
      text: row[SAP_COL_TEXT] ?? "",
    });
  }
  return rows;
}

function parseBankSheet(aoa) {
  const rows = [];
  for (let i = BANK_FIRST_DATA_ROW; i < aoa.length; i++) {
    const row = aoa[i];
    if (!row) continue;
    const dateRaw = row[BANK_COL_DATE];
    const debit = toNum(row[BANK_COL_DEBIT]);
    const credit = toNum(row[BANK_COL_CREDIT]);
    if (dateRaw === null || dateRaw === undefined) continue;
    if (isNaN(debit) && isNaN(credit)) continue;
    const d = parseAnyDate(dateRaw);
    if (!d) continue;
    const amt = (isNaN(credit) ? 0 : credit) - (isNaN(debit) ? 0 : debit);
    rows.push({
      day: d.day,
      month: d.month,
      dateObj: d.dateObj,
      dateLabel: `${pad2(d.day)}/${pad2(d.month)}`,
      amount: round2(amt),
      channel: row[BANK_COL_CHANNEL] ?? "",
      detail: row[BANK_COL_DETAIL] ?? "",
    });
  }
  return rows;
}

// ============================================================
// Matching engine (mirrors the Python script)
// ============================================================
function reconcile(sapRows, bankRows, tolerance = TOLERANCE_DAYS) {
  const sapByAmt = new Map();
  sapRows.forEach((r, i) => {
    if (!sapByAmt.has(r.amount)) sapByAmt.set(r.amount, []);
    sapByAmt.get(r.amount).push(i);
  });
  const bankByAmt = new Map();
  bankRows.forEach((r, i) => {
    if (!bankByAmt.has(r.amount)) bankByAmt.set(r.amount, []);
    bankByAmt.get(r.amount).push(i);
  });

  const matches = [];
  for (const [amt, sList] of sapByAmt) {
    const bList = (bankByAmt.get(amt) || []).slice();
    if (bList.length === 0) continue;
    const sSorted = [...sList].sort(
      (a, b) => sapRows[a].dateObj - sapRows[b].dateObj
    );
    for (const si of sSorted) {
      if (bList.length === 0) break;
      let bestPos = 0;
      let bestDiff = Infinity;
      bList.forEach((bi, pos) => {
        const diff =
          Math.abs(sapRows[si].dateObj - bankRows[bi].dateObj) / 86400000;
        if (diff < bestDiff) {
          bestDiff = diff;
          bestPos = pos;
        }
      });
      const bi = bList[bestPos];
      matches.push({ si, bi, diff: bestDiff });
      bList.splice(bestPos, 1);
    }
  }

  const confirmed = matches.filter((m) => m.diff <= tolerance);
  const matchedS = new Set(confirmed.map((m) => m.si));
  const matchedB = new Set(confirmed.map((m) => m.bi));
  const sapUnmatched = sapRows
    .map((_, i) => i)
    .filter((i) => !matchedS.has(i));
  const bankUnmatched = bankRows
    .map((_, i) => i)
    .filter((i) => !matchedB.has(i));
  const dateGap = confirmed.filter((m) => m.diff >= 1);

  return { confirmed, dateGap, sapUnmatched, bankUnmatched };
}

// ============================================================
// Manual-match suggestions ("จับคู่ยอดต่าง" tab) — the automatic matcher
// above only ever pairs one SAP row with one Bank row of the *exact same*
// amount. This looks for the harder cases it can't: one side split across
// up to 2 rows that together approach the other side's amount (the
// 610,000 + 610,000 = 1,220,000 case), or a single pair that's merely
// close (not exact) in amount — both scored by how close the amounts and
// dates actually are, not just "found something".
// ============================================================
const SUGGEST_DATE_WINDOW_DAYS = 10; // wider than the 3-day auto-match tolerance — these are the leftovers auto-matching already gave up on
const SUGGEST_MAX_COMBO = 2; // pairs only; the candidate pool cap below keeps triples+ from being combinatorially expensive, so this is where we draw the line
const SUGGEST_CANDIDATE_POOL = 25; // nearest-by-date rows considered per target, before combining
const SUGGEST_MAX_REL_DIFF = 0.15; // amounts more than 15% apart aren't worth suggesting at all
const SUGGEST_MIN_SCORE = 50;
const SUGGEST_MAX_RESULTS = 30;

function daysBetween(a, b) {
  return Math.abs(a - b) / 86400000;
}

// All non-empty subsets of `arr` up to size `maxSize`, as arrays of the
// original elements (not indices) — small enough inputs (see
// SUGGEST_CANDIDATE_POOL) that plain nested-loop generation beats pulling
// in a combinatorics dependency for this one call site.
function combosUpTo(arr, maxSize) {
  const out = [];
  const n = arr.length;
  for (let size = 1; size <= Math.min(maxSize, n); size++) {
    const idxs = Array.from({ length: size }, (_, i) => i);
    while (true) {
      out.push(idxs.map((i) => arr[i]));
      let i = size - 1;
      while (i >= 0 && idxs[i] === n - size + i) i--;
      if (i < 0) break;
      idxs[i]++;
      for (let j = i + 1; j < size; j++) idxs[j] = idxs[j - 1] + 1;
    }
  }
  return out;
}

// Weighted 70/30 toward amount closeness over date closeness — a same-day
// pair with a 10% amount gap is a weaker candidate than a 9-day-apart pair
// that sums exactly, since the amount match is the stronger signal for
// "this is really the same transaction". Returns null if the amounts are
// too far apart to even bother scoring (see SUGGEST_MAX_REL_DIFF).
function scoreCombo(totalA, totalB, maxSpreadDays) {
  const diff = round2(totalA - totalB);
  const relDiff = Math.abs(diff) / Math.max(Math.abs(totalA), Math.abs(totalB), 1);
  if (relDiff > SUGGEST_MAX_REL_DIFF) return null;
  const amountScore = 100 * (1 - relDiff / SUGGEST_MAX_REL_DIFF);
  const dateScore = Math.max(0, 100 * (1 - maxSpreadDays / SUGGEST_DATE_WINDOW_DAYS));
  return { diff, value: Math.round(amountScore * 0.7 + dateScore * 0.3) };
}

// dd/mm -> Date, year pinned to 2000 like parseAnyDate's own dateObj — the
// source files carry no reliable year (see parseAnyDate's comment), so
// every date in this app is compared as month+day only. Working off the
// label directly (rather than needing the raw parsed rows' real dateObj)
// is what lets this run against a *saved* report's flattened payload too
// (ReconciliationHistory.jsx), which never has the raw rows available.
function dateObjFromLabel(label) {
  const [d, m] = label.split("/").map(Number);
  return new Date(2000, m - 1, d);
}

// `sapPool`/`bankPool` are the flattened rows still showing as unmatched
// (payload.sapUnmatched/bankUnmatched, post any already-confirmed manual
// matches) — each just needs its usual `date` ("dd/mm") label, amount, and
// sapIdx/bankIdx identity. Searches both directions (SAP combo -> one Bank
// row, and the mirror) so it catches a split on either side, then greedily
// keeps the highest-scoring, non-overlapping suggestions — a row already
// claimed by a better-scoring suggestion doesn't also show up in a worse one.
export function suggestManualMatches(sapPool, bankPool) {
  const sapDateOf = (r) => dateObjFromLabel(r.date);
  const bankDateOf = (r) => dateObjFromLabel(r.date);
  const candidates = [];

  for (const bankRow of bankPool) {
    const bDate = bankDateOf(bankRow);
    const near = sapPool
      .map((r) => ({ r, gap: daysBetween(sapDateOf(r), bDate) }))
      .filter((x) => x.gap <= SUGGEST_DATE_WINDOW_DAYS)
      .sort((a, b) => a.gap - b.gap)
      .slice(0, SUGGEST_CANDIDATE_POOL)
      .map((x) => x.r);
    for (const combo of combosUpTo(near, SUGGEST_MAX_COMBO)) {
      const sapTotal = round2(combo.reduce((s, r) => s + r.amount, 0));
      const maxSpread = Math.max(...combo.map((r) => daysBetween(sapDateOf(r), bDate)));
      const s = scoreCombo(sapTotal, bankRow.amount, maxSpread);
      if (!s || s.value < SUGGEST_MIN_SCORE) continue;
      candidates.push({ sapRows: combo, bankRows: [bankRow], sapTotal, bankTotal: bankRow.amount, diff: s.diff, score: s.value });
    }
  }

  for (const sapRow of sapPool) {
    const sDate = sapDateOf(sapRow);
    const near = bankPool
      .map((r) => ({ r, gap: daysBetween(bankDateOf(r), sDate) }))
      .filter((x) => x.gap <= SUGGEST_DATE_WINDOW_DAYS)
      .sort((a, b) => a.gap - b.gap)
      .slice(0, SUGGEST_CANDIDATE_POOL)
      .map((x) => x.r);
    for (const combo of combosUpTo(near, SUGGEST_MAX_COMBO)) {
      if (combo.length === 1) continue; // 1:1 pairs already come out of the SAP->Bank direction above — skip the duplicate
      const bankTotal = round2(combo.reduce((s, r) => s + r.amount, 0));
      const maxSpread = Math.max(...combo.map((r) => daysBetween(bankDateOf(r), sDate)));
      const s = scoreCombo(sapRow.amount, bankTotal, maxSpread);
      if (!s || s.value < SUGGEST_MIN_SCORE) continue;
      candidates.push({ sapRows: [sapRow], bankRows: combo, sapTotal: sapRow.amount, bankTotal, diff: s.diff, score: s.value });
    }
  }

  candidates.sort((a, b) => b.score - a.score);
  const usedSap = new Set();
  const usedBank = new Set();
  const results = [];
  for (const c of candidates) {
    if (c.sapRows.some((r) => usedSap.has(r.sapIdx))) continue;
    if (c.bankRows.some((r) => usedBank.has(r.bankIdx))) continue;
    results.push({ id: `sug-${c.sapRows.map((r) => r.sapIdx).join("_")}-${c.bankRows.map((r) => r.bankIdx).join("_")}`, ...c });
    c.sapRows.forEach((r) => usedSap.add(r.sapIdx));
    c.bankRows.forEach((r) => usedBank.add(r.bankIdx));
    if (results.length >= SUGGEST_MAX_RESULTS) break;
  }
  return results;
}

// Flattens the matched result set into the exact row shapes each DataTable
// renders — used both to save a run (so a saved report replays identically
// without the source files) and by the live view itself, so the two never
// drift apart.
function buildResultPayload(result) {
  return {
    matched: result.confirmed.map((m) => ({
      dateSap: result.sapRows[m.si].dateLabel,
      dateBank: result.bankRows[m.bi].dateLabel,
      amount: result.sapRows[m.si].amount,
      docno: result.sapRows[m.si].docno,
      textSap: result.sapRows[m.si].text,
      detailBank: result.bankRows[m.bi].detail,
    })),
    // sapIdx/bankIdx (the row's position in result.sapRows/bankRows) is a
    // stable identity carried into the flattened row — manual matching
    // (see ManualMatchPanel) needs a way to say "this exact row" that
    // survives the table's own sorting/searching, which a plain array
    // index into the *filtered* rows wouldn't.
    sapUnmatched: result.sapUnmatched.map((i) => ({
      sapIdx: i,
      date: result.sapRows[i].dateLabel,
      amount: result.sapRows[i].amount,
      docno: result.sapRows[i].docno,
      text: result.sapRows[i].text,
    })),
    bankUnmatched: result.bankUnmatched.map((i) => ({
      bankIdx: i,
      date: result.bankRows[i].dateLabel,
      amount: result.bankRows[i].amount,
      channel: result.bankRows[i].channel,
      detail: result.bankRows[i].detail,
    })),
    dateGap: result.dateGap.map((m) => ({
      diff: m.diff,
      dateSap: result.sapRows[m.si].dateLabel,
      dateBank: result.bankRows[m.bi].dateLabel,
      amount: result.sapRows[m.si].amount,
      textSap: result.sapRows[m.si].text,
    })),
  };
}

// Per-day totals across ALL parsed rows (not just matched ones) — lets you
// spot which specific day is out of balance without scrolling a 1,000+ row
// table. Keyed by "dd/mm" label since the source files carry no reliable
// year (see parseAnyDate).
function buildDailySummary(sapRows, bankRows) {
  const map = new Map();
  const bucket = (label, dateObj) => {
    if (!map.has(label)) {
      map.set(label, { date: label, dateObj, sapTotal: 0, sapCount: 0, bankTotal: 0, bankCount: 0 });
    }
    return map.get(label);
  };
  sapRows.forEach((r) => {
    const e = bucket(r.dateLabel, r.dateObj);
    e.sapTotal = round2(e.sapTotal + r.amount);
    e.sapCount += 1;
  });
  bankRows.forEach((r) => {
    const e = bucket(r.dateLabel, r.dateObj);
    e.bankTotal = round2(e.bankTotal + r.amount);
    e.bankCount += 1;
  });
  return Array.from(map.values())
    .map((e) => ({ ...e, diff: round2(e.sapTotal - e.bankTotal) }))
    .sort((a, b) => a.dateObj - b.dateObj);
}

// "dd/mm" -> mm*100+dd, and "YYYY-MM-DD" (native <input type="date">) -> the
// same shape — lets a from/to range filter rows by month+day only, since
// the source files carry no reliable year to compare against. Exported so
// ReconciliationHistory.jsx can apply the identical filter/summary logic to
// a saved report's payload.
export function labelToMD(label) {
  const [d, m] = label.split("/").map(Number);
  return m * 100 + d;
}
export function isoToMD(iso) {
  if (!iso) return null;
  const [, m, d] = iso.split("-").map(Number);
  return m * 100 + d;
}
export function inDateRange(label, fromMD, toMD) {
  const md = labelToMD(label);
  if (fromMD != null && md < fromMD) return false;
  if (toMD != null && md > toMD) return false;
  return true;
}

// ============================================================
// Excel export
// ============================================================
// Works directly off the flattened `payload` shape (buildResultPayload's
// output — matched/sapUnmatched/bankUnmatched/dateGap) and a daily-summary
// array, not raw sapRows/bankRows/index-pairs — so the exact same function
// exports either a live comparison or a saved report loaded from the
// database (ReconciliationHistory.jsx), which only ever has the flattened
// form available.
export function exportWorkbook({ payload, dailySummary, sapCount, bankCount, sapTotal, bankTotal, filename }) {
  const manualMatched = payload.manualMatched || [];
  const wb = xlsxUtils.book_new();

  const summaryAoa = [
    ["สรุปผลการตรวจสอบ (Reconciliation) SAP vs Bank Statement"],
    [`จับคู่วันที่ + จำนวนเงิน (อนุโลมส่วนต่างวันที่ไม่เกิน ${TOLERANCE_DAYS} วัน)`],
    [],
    ["รายการ", "SAP", "Bank"],
    ["จำนวนรายการทั้งหมด", sapCount, bankCount],
    ["ผลรวมยอดเงินทั้งหมด (บาท)", sapTotal, bankTotal],
    ["จำนวนรายการที่จับคู่ได้ตรงกัน", payload.matched.length, payload.matched.length],
    ["จำนวนรายการที่หาคู่ไม่เจอ", payload.sapUnmatched.length, payload.bankUnmatched.length],
    [],
    ["วิธีการเปรียบเทียบ (Matching Logic)"],
    ["1. เทียบ \"จำนวนเงิน\" ที่เท่ากันเป๊ะระหว่าง SAP และ Bank ก่อน (จัดกลุ่มตามยอดเงิน)"],
    [`2. ในแต่ละกลุ่มยอดเงินเดียวกัน ใช้ SAP เป็นตัวตั้ง ไล่หา Bank ที่วันที่ใกล้เคียงที่สุดมาจับคู่ให้ (ยอมรับส่วนต่างได้ไม่เกิน ${TOLERANCE_DAYS} วัน)`],
    ["3. Bank แถวที่ถูกจับคู่ไปแล้ว จะไม่ถูกนำไปจับคู่ซ้ำกับ SAP แถวอื่นอีก (จับคู่ได้ครั้งเดียวต่อรายการ)"],
    ["4. แถวที่หาคู่ไม่เจอ (ยอดไม่ตรง หรือวันที่ห่างเกินกำหนด) จะแยกไปอยู่ชีท \"SAP หาคู่ไม่เจอ\" / \"Bank หาคู่ไม่เจอ\""],
    ["5. คู่ที่จับคู่ได้แต่วันที่ต่างกันตั้งแต่ 1 วันขึ้นไป จะถูกโชว์ซ้ำอีกทีในชีท \"วันที่ไม่ตรงกัน\" เพื่อให้ตรวจสอบง่ายขึ้น"],
    [],
    ["ข้อควรระวัง — เมื่อยอดเงินซ้ำกันหลายรายการ"],
    ["ถ้ามีหลายรายการที่ \"จำนวนเงินเท่ากัน\" และวันที่ใกล้เคียงกัน (เช่น ค่าเช่างวดละ 1,000 บาทจากหลายห้อง) ระบบจะจับคู่ตามลำดับวันที่ใกล้ที่สุดเท่านั้น"],
    ["ไม่ได้อ่านหรือเทียบชื่อ/รายละเอียดใดๆ เลย เพราะข้อมูล SAP กับ Bank ไม่มีรหัสอ้างอิงร่วมกันให้เทียบได้"],
    ["ดังนั้นในชีท \"จับคู่ได้\" คอลัมน์ \"รายละเอียด SAP\" กับ \"รายละเอียด Bank\" ในแถวเดียวกัน อาจไม่ใช่คู่ที่ตรงกันจริงในทางบัญชี — ไม่ควรใช้อ้างอิงว่าใครโอนตรงกับรายการไหนเป๊ะๆ"],
    ["ยอดรวมและจำนวนรายการโดยรวมยังคงถูกต้องเสมอ (เงินเข้า-ออกครบไม่ขาดไม่เกิน) กระทบเฉพาะการจับคู่ระดับรายบุคคลในกรณียอดซ้ำเท่านั้น"],
  ];
  const wsSummary = xlsxUtils.aoa_to_sheet(summaryAoa);
  wsSummary["!cols"] = [{ wch: 100 }, { wch: 16 }, { wch: 16 }];
  xlsxUtils.book_append_sheet(wb, wsSummary, "สรุปผล");

  const matchedAoa = [
    ["หมายเหตุ: หากยอดเงินซ้ำกันหลายรายการในวันใกล้เคียงกัน คอลัมน์ \"รายละเอียด SAP\" และ \"รายละเอียด Bank\" ในแถวเดียวกัน อาจไม่ใช่คู่ที่ตรงกันจริง — ระบบจับคู่ด้วยจำนวนเงิน+วันที่ใกล้สุดเท่านั้น ไม่ได้อ้างอิงชื่อ/รายละเอียด ดูวิธีการทำงานเต็มๆ ที่ชีท \"สรุปผล\""],
    [],
    ["วันที่ SAP", "วันที่ Bank", "จำนวนเงิน", "เลขที่เอกสาร (SAP)", "รายละเอียด SAP", "รายละเอียด Bank"],
    ...payload.matched.map((r) => [r.dateSap, r.dateBank, r.amount, r.docno, r.textSap, r.detailBank]),
  ];
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(matchedAoa), "จับคู่ได้");

  const sapUnAoa = [
    ["วันที่ (SAP)", "จำนวนเงิน", "เลขที่เอกสาร", "รายละเอียด"],
    ...payload.sapUnmatched.map((r) => [r.date, r.amount, r.docno, r.text]),
  ];
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(sapUnAoa), "SAP หาคู่ไม่เจอ");

  const bankUnAoa = [
    ["วันที่ (Bank)", "จำนวนเงิน", "ช่องทาง", "รายละเอียด"],
    ...payload.bankUnmatched.map((r) => [r.date, r.amount, r.channel, r.detail]),
  ];
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(bankUnAoa), "Bank หาคู่ไม่เจอ");

  // One row per member (SAP or Bank side), grouped by "กลุ่มที่" — mirrors
  // how ManualMatchPanel renders each group as a card, just flattened for
  // a spreadsheet.
  const manualAoa = [
    ["กลุ่มที่", "ฝั่ง", "วันที่", "จำนวนเงิน", "เลขที่เอกสาร/ช่องทาง", "รายละเอียด", "ยอด SAP รวม", "ยอด Bank รวม", "ส่วนต่าง"],
    ...manualMatched.flatMap((g, gi) => [
      ...g.sapRows.map((r) => [gi + 1, "SAP", r.date, r.amount, r.docno, r.text, g.sapTotal, g.bankTotal, g.diff]),
      ...g.bankRows.map((r) => [gi + 1, "Bank", r.date, r.amount, r.channel, r.detail, g.sapTotal, g.bankTotal, g.diff]),
    ]),
  ];
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(manualAoa), "จับคู่ยอดต่าง");

  const gapAoa = [
    ["ส่วนต่างวัน (วัน)", "วันที่ SAP", "วันที่ Bank", "จำนวนเงิน", "รายละเอียด SAP"],
    ...payload.dateGap.map((r) => [r.diff, r.dateSap, r.dateBank, r.amount, r.textSap]),
  ];
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(gapAoa), "วันที่ไม่ตรงกัน");

  const dailyAoa = [
    ["วันที่", "ยอด SAP", "จำนวนรายการ SAP", "ยอด Bank", "จำนวนรายการ Bank", "ส่วนต่าง"],
    ...dailySummary.map((r) => [r.date, r.sapTotal, r.sapCount, r.bankTotal, r.bankCount, r.diff]),
  ];
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(dailyAoa), "สรุปรายวัน");

  writeXLSXFile(wb, filename || "CSA_Reconciliation.xlsx");
}

// ============================================================
// UI bits
// ============================================================
export const thb = (n) =>
  n.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// `onFiles` gets the full drop/selection, not just one file — dropping (or
// multi-selecting) both SAP and Bank files together onto either box works,
// since the parent auto-sorts by filename (see classifyFile).
function Dropzone({ label, sublabel, file, onFiles, accent }) {
  const [dragOver, setDragOver] = useState(false);
  const inputId = label.replace(/\s/g, "-");

  return (
    <label
      htmlFor={inputId}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length) onFiles(files);
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        padding: "28px 20px",
        borderRadius: 14,
        border: `1.5px dashed ${dragOver ? accent : "#C8CCD4"}`,
        background: dragOver ? `${accent}14` : file ? "#FFFFFF" : "#FBFBF9",
        cursor: "pointer",
        transition: "all .15s ease",
        minHeight: 150,
        textAlign: "center",
      }}
    >
      <input
        id={inputId}
        type="file"
        accept=".xlsx,.xls"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) onFiles(files);
          e.target.value = "";
        }}
      />
      {file ? (
        <>
          <FileSpreadsheet size={26} color={accent} strokeWidth={1.8} />
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, color: "#132A3A", fontWeight: 600 }}>
            {file.name}
          </div>
          <div style={{ fontSize: 11.5, color: "#8B93A1" }}>
            {(file.size / 1024).toFixed(0)} KB — คลิกเพื่อเปลี่ยนไฟล์
          </div>
        </>
      ) : (
        <>
          <Upload size={24} color="#8B93A1" strokeWidth={1.8} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "#132A3A" }}>{label}</div>
          <div style={{ fontSize: 11.5, color: "#8B93A1" }}>{sublabel}</div>
        </>
      )}
    </label>
  );
}

export function StatChip({ value, label, tone }) {
  const tones = {
    navy: { bg: "#EDF1F5", fg: "#132A3A" },
    green: { bg: "#E7F3EC", fg: "#1F6650" },
    red: { bg: "#FBEAE8", fg: "#B3261E" },
    amber: { bg: "#FBF1DE", fg: "#8A5A00" },
    purple: { bg: "#F1EAFB", fg: "#5B2FA6" },
  };
  const t = tones[tone] || tones.navy;
  return (
    <div style={{ background: t.bg, borderRadius: 10, padding: "12px 16px", flex: 1, minWidth: 130 }}>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, fontWeight: 700, color: t.fg, lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: 11.5, color: t.fg, opacity: 0.8, marginTop: 4 }}>{label}</div>
    </div>
  );
}

// Exports exactly what's currently visible in one DataTable (respects its
// own search box) as a single-sheet workbook — separate from
// exportWorkbook's full multi-sheet export of the whole (unfiltered)
// result, for when you just want the one table/view you're looking at.
function exportTableRows(columns, rows, filename) {
  const wb = xlsxUtils.book_new();
  const aoa = [
    columns.map((c) => c.label),
    ...rows.map((r) => columns.map((c) => r[c.key] ?? "")),
  ];
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(aoa), "ข้อมูล");
  writeXLSXFile(wb, filename || "CSA_ข้อมูล.xlsx");
}

// selectKey/selectedIds/onToggleRow/onToggleAll are all optional — passing
// none of them renders the table exactly as before (no checkbox column).
// Only the two "หาคู่ไม่เจอ" tabs pass them, to build a manual match (see
// ManualMatchPanel and the sapUn/bankUn tab wiring in Reconciliation()).
export function DataTable({ columns, rows, emptyText, accent, exportFilename, selectKey, selectedIds, onToggleRow, onToggleAll }) {
  const selectable = !!selectKey;
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  const searched = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) =>
      columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q))
    );
  }, [rows, query, columns]);

  // Click a header to sort by it (asc -> desc -> back to unsorted). Date
  // columns hold "dd/mm" labels, which sort wrong lexically across months
  // (e.g. "05/07" < "17/06"), so those go through labelToMD instead.
  const filtered = useMemo(() => {
    if (!sortKey) return searched;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return searched;
    const dir = sortDir === "asc" ? 1 : -1;
    const valueOf = (r) => {
      const v = r[sortKey];
      if (col.date) return labelToMD(v) ?? -Infinity;
      if (col.numeric) return Number(v) || 0;
      return String(v ?? "").toLowerCase();
    };
    return [...searched].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [searched, sortKey, sortDir, columns]);

  const toggleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir("asc");
    }
  };

  // Sum of every numeric column across the currently-filtered rows, shown
  // as a footer row — money columns via thb(), count columns as plain
  // integers, so it always matches "แสดง X / Y รายการ" above it.
  const totals = useMemo(() => {
    const sums = {};
    columns.forEach((c) => {
      if (c.numeric) sums[c.key] = filtered.reduce((s, r) => s + (Number(r[c.key]) || 0), 0);
    });
    return sums;
  }, [filtered, columns]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, color: "#8B93A1" }}>
          แสดง {filtered.length.toLocaleString()} / {rows.length.toLocaleString()} รายการ
        </span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ค้นหา..."
            style={{
              fontSize: 12.5,
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid #DADFE6",
              outline: "none",
              width: 180,
              fontFamily: "inherit",
            }}
          />
          <button
            onClick={() => exportTableRows(columns, filtered, exportFilename)}
            title="ดาวน์โหลดตารางนี้เป็น Excel"
            style={{
              display: "flex", alignItems: "center", gap: 5, padding: "6px 10px", borderRadius: 8,
              border: "1px solid #1F6650", background: "#E7F3EC", color: "#1F6650",
              fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            <Download size={12} /> Excel
          </button>
        </div>
      </div>
      <div style={{ maxHeight: 420, overflow: "auto", border: "1px solid #E7EAEF", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead style={{ position: "sticky", top: 0, background: "#132A3A", zIndex: 1 }}>
            <tr>
              {selectable && (
                <th style={{ padding: "9px 10px", width: 1 }}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((r) => selectedIds.has(r[selectKey]))}
                    onChange={(e) => onToggleAll(filtered.map((r) => r[selectKey]), e.target.checked)}
                    title="เลือกทั้งหมดที่แสดงอยู่"
                  />
                </th>
              )}
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  title="คลิกเพื่อเรียงลำดับ มาก/น้อย"
                  style={{
                    textAlign: c.numeric ? "right" : "left",
                    padding: "9px 12px",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 11.5,
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  {c.label}
                  <span style={{ marginLeft: 4, fontSize: 10, opacity: sortKey === c.key ? 1 : 0.35 }}>
                    {sortKey === c.key ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} style={{ padding: 24, textAlign: "center", color: "#8B93A1" }}>
                  {emptyText}
                </td>
              </tr>
            ) : (
              filtered.map((r, idx) => (
                <tr key={idx} style={{ borderTop: "1px solid #EEF0F3", background: idx % 2 ? "#FAFBFC" : "#fff" }}>
                  {selectable && (
                    <td style={{ padding: "8px 10px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r[selectKey])}
                        onChange={() => onToggleRow(r[selectKey])}
                      />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      style={{
                        padding: "8px 12px",
                        textAlign: c.numeric ? "right" : "left",
                        fontFamily: c.numeric ? "'IBM Plex Mono', monospace" : "inherit",
                        color: c.numeric && r[c.key] < 0 ? accent?.negative || "#B3261E" : "#2A3441",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.count ? Number(r[c.key] ?? 0).toLocaleString("th-TH") : c.numeric ? thb(r[c.key]) : String(r[c.key] ?? "")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {filtered.length > 0 && (() => {
            let labelPlaced = false;
            return (
              <tfoot>
                <tr style={{ borderTop: "2px solid #DADFE6", background: "#F6F8FA" }}>
                  {selectable && <td />}
                  {columns.map((c) => {
                    let content = "";
                    if (c.numeric) {
                      content = c.count ? Number(totals[c.key] || 0).toLocaleString("th-TH") : thb(totals[c.key] || 0);
                    } else if (!labelPlaced) {
                      content = `รวม ${filtered.length.toLocaleString()} รายการ`;
                      labelPlaced = true;
                    }
                    return (
                      <td
                        key={c.key}
                        style={{
                          padding: "9px 12px",
                          textAlign: c.numeric ? "right" : "left",
                          fontFamily: c.numeric ? "'IBM Plex Mono', monospace" : "inherit",
                          fontWeight: 700,
                          color: "#132A3A",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </div>
    </div>
  );
}

// Shared card body (SAP column | Bank column, each with its own running
// total) for both a confirmed group ("จับคู่ยอดต่าง" tab, via
// ManualMatchPanel) and a not-yet-accepted suggestion (SuggestionCard) —
// `headerRight` is whatever badge/buttons belong on that specific variant,
// since a confirmed group shows a diff badge + "ยกเลิก" while a suggestion
// shows a score badge + "ยืนยัน"/"ข้าม".
function MatchGroupCard({ sapRows, bankRows, sapTotal, bankTotal, title, headerRight }) {
  return (
    <div style={{ border: "1px solid #E7EAEF", borderRadius: 10, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", background: "#F6F8FA", borderBottom: "1px solid #E7EAEF", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#132A3A" }}>
          {title} · SAP {sapRows.length} รายการ ↔ Bank {bankRows.length} รายการ
        </span>
        {headerRight}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
        <div style={{ padding: "10px 14px", borderRight: "1px solid #EEF0F3" }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#8B93A1", marginBottom: 6 }}>SAP</div>
          {sapRows.map((r) => (
            <div key={r.sapIdx} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, padding: "3px 0" }}>
              <span style={{ color: "#63697A" }}>{r.date} · {r.docno || "-"} {r.text ? `· ${r.text}` : ""}</span>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, whiteSpace: "nowrap" }}>฿{thb(r.amount)}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, marginTop: 6, paddingTop: 6, borderTop: "1px dashed #DADFE6" }}>
            <span>รวม SAP</span>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>฿{thb(sapTotal)}</span>
          </div>
        </div>
        <div style={{ padding: "10px 14px" }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#8B93A1", marginBottom: 6 }}>Bank</div>
          {bankRows.map((r) => (
            <div key={r.bankIdx} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, padding: "3px 0" }}>
              <span style={{ color: "#63697A" }}>{r.date} · {r.channel || "-"} {r.detail ? `· ${r.detail}` : ""}</span>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, whiteSpace: "nowrap" }}>฿{thb(r.amount)}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, marginTop: 6, paddingTop: 6, borderTop: "1px dashed #DADFE6" }}>
            <span>รวม Bank</span>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>฿{thb(bankTotal)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// One card per manually-confirmed group ("จับคู่ยอดต่าง" tab) — unlike
// DataTable's flat rows, a group bundles N SAP rows against M Bank rows
// (e.g. two 610,000 SAP entries against one 1,220,000 Bank entry), so it
// needs its own layout rather than being forced into a uniform-column
// table. `onUndo` is omitted (undefined) for the read-only view in
// ReconciliationHistory.jsx — a saved report can't be edited.
export function ManualMatchPanel({ groups, onUndo }) {
  if (!groups.length) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B93A1", fontSize: 13 }}>ยังไม่มีรายการที่จับคู่ยอดต่างด้วยตนเอง</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {groups.map((g, gi) => (
        <MatchGroupCard
          key={g.id ?? gi}
          sapRows={g.sapRows}
          bankRows={g.bankRows}
          sapTotal={g.sapTotal}
          bankTotal={g.bankTotal}
          title={`จับคู่ #${gi + 1}`}
          headerRight={
            <>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                background: g.diff === 0 ? "#E7F3EC" : "#FBF1DE",
                color: g.diff === 0 ? "#1F6650" : "#8A5A00",
              }}>
                ส่วนต่าง ฿{thb(Math.abs(g.diff))}
              </span>
              <div style={{ flex: 1 }} />
              {onUndo && (
                <button
                  onClick={() => onUndo(g.id)}
                  style={{ fontSize: 11.5, fontWeight: 600, color: "#B3261E", background: "transparent", border: "none", cursor: "pointer" }}
                >
                  ยกเลิกการจับคู่
                </button>
              )}
            </>
          }
        />
      ))}
    </div>
  );
}

// One card per not-yet-accepted suggestion (see suggestManualMatches) —
// same body as a confirmed group, but the header shows a % score instead
// of a diff badge, and offers ยืนยัน/ข้าม instead of ยกเลิก.
export function SuggestionCard({ suggestion: s, onAccept, onDismiss }) {
  const scoreTone = s.score >= 85 ? { bg: "#E7F3EC", fg: "#1F6650" } : s.score >= 65 ? { bg: "#FBF1DE", fg: "#8A5A00" } : { bg: "#FBEAE8", fg: "#B3261E" };
  return (
    <MatchGroupCard
      sapRows={s.sapRows}
      bankRows={s.bankRows}
      sapTotal={s.sapTotal}
      bankTotal={s.bankTotal}
      title="ข้อเสนอแนะ"
      headerRight={
        <>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: scoreTone.bg, color: scoreTone.fg }}>
            โอกาสตรงกัน {s.score}%
          </span>
          <span style={{ fontSize: 11, color: "#8B93A1" }}>ส่วนต่าง ฿{thb(Math.abs(s.diff))}</span>
          <div style={{ flex: 1 }} />
          <button
            onClick={onDismiss}
            style={{ fontSize: 11.5, fontWeight: 600, color: "#63697A", background: "transparent", border: "none", cursor: "pointer" }}
          >
            ข้าม
          </button>
          <button
            onClick={onAccept}
            style={{ fontSize: 11.5, fontWeight: 700, color: "#fff", background: "#5B2FA6", border: "none", borderRadius: 7, padding: "5px 12px", cursor: "pointer" }}
          >
            ยืนยัน
          </button>
        </>
      }
    />
  );
}

// Sticky selection tray shown on the two "หาคู่ไม่เจอ" tabs — accumulates
// checked rows from *both* tabs (selection survives switching between
// them, since that's the whole point: pick SAP rows on one tab, Bank rows
// on the other, then confirm once both sides have something selected).
export function ManualMatchTray({ selectedSapRows, selectedBankRows, onConfirm, onClear }) {
  const sapTotal = round2(selectedSapRows.reduce((s, r) => s + r.amount, 0));
  const bankTotal = round2(selectedBankRows.reduce((s, r) => s + r.amount, 0));
  const diff = round2(sapTotal - bankTotal);
  const canConfirm = selectedSapRows.length > 0 && selectedBankRows.length > 0;
  if (selectedSapRows.length === 0 && selectedBankRows.length === 0) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
      background: "#F1EAFB", border: "1px solid #DCC9F5", borderRadius: 12, padding: "12px 16px", marginBottom: 12,
    }}>
      <span style={{ fontSize: 12.5, color: "#5B2FA6" }}>
        เลือกจาก SAP <b>{selectedSapRows.length}</b> รายการ (฿{thb(sapTotal)}) · เลือกจาก Bank <b>{selectedBankRows.length}</b> รายการ (฿{thb(bankTotal)})
        {canConfirm && <> · ส่วนต่าง <b style={{ fontFamily: "'IBM Plex Mono', monospace" }}>฿{thb(Math.abs(diff))}</b></>}
      </span>
      <div style={{ flex: 1 }} />
      <button
        onClick={onClear}
        style={{ fontSize: 12, fontWeight: 600, color: "#63697A", background: "transparent", border: "none", cursor: "pointer" }}
      >
        ล้างการเลือก
      </button>
      <button
        onClick={onConfirm}
        disabled={!canConfirm}
        title={canConfirm ? "" : "ต้องเลือกอย่างน้อย 1 รายการจากทั้งสองฝั่ง"}
        style={{
          display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "none",
          background: canConfirm ? "#5B2FA6" : "#C8CCD4", color: "#fff", fontSize: 12.5, fontWeight: 600,
          cursor: canConfirm ? "pointer" : "not-allowed",
        }}
      >
        จับคู่รายการที่เลือก
      </button>
    </div>
  );
}

// ============================================================
// Main page
// ============================================================
export default function Reconciliation() {
  const { addToast } = useToast();
  const [sapFile, setSapFile] = useState(null);
  const [bankFile, setBankFile] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | processing | done | error
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState("matched");
  const [period, setPeriod] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Manual matching ("จับคู่ยอดต่าง"): confirmed groups, plus the in-progress
  // selection on the two "หาคู่ไม่เจอ" tabs that a confirm turns into a group.
  const [manualMatches, setManualMatches] = useState([]);
  const [selectedSap, setSelectedSap] = useState(() => new Set());
  const [selectedBank, setSelectedBank] = useState(() => new Set());
  // "วิเคราะห์หาคู่ที่เป็นไปได้" — suggestions is null until the button's been
  // clicked (so the tab starts empty, not mid-computation); dismissed ones
  // stay hidden even though they're still technically valid until the row
  // data changes shape (a fresh handleProcess/handleReset run).
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [dismissedSuggestions, setDismissedSuggestions] = useState(() => new Set());

  // Classifies a dropped/selected file by its name — the real source files are
  // always named with "SAP" or "Bank" in them (e.g. "SAP - Statement มิ.ย.2569.XLS",
  // "CSA Bank Statement M.06.xlsx"), so this lets either dropzone accept both
  // files at once and sort them into the right slot automatically.
  const classifyFile = (f) => {
    const name = f.name.toLowerCase();
    if (name.includes("sap")) return "sap";
    if (name.includes("bank")) return "bank";
    return null;
  };

  const handleFilesFor = useCallback((targetSlot) => (files) => {
    if (!files || !files.length) return;
    if (files.length === 1) {
      const slot = classifyFile(files[0]) || targetSlot;
      if (slot === "sap") setSapFile(files[0]);
      else setBankFile(files[0]);
      return;
    }
    // Multiple files dropped/selected together: sort each by filename, then
    // fall back to fill order for anything ambiguous.
    const leftovers = [];
    let sapPick = null;
    let bankPick = null;
    files.forEach((f) => {
      const slot = classifyFile(f);
      if (slot === "sap" && !sapPick) sapPick = f;
      else if (slot === "bank" && !bankPick) bankPick = f;
      else leftovers.push(f);
    });
    while (leftovers.length && (!sapPick || !bankPick)) {
      if (!sapPick) sapPick = leftovers.shift();
      else if (!bankPick) bankPick = leftovers.shift();
    }
    if (sapPick) setSapFile(sapPick);
    if (bankPick) setBankFile(bankPick);
  }, []);

  const canProcess = sapFile && bankFile && status !== "processing";
  // Rows already folded into a manual match group are pulled out of
  // sapUnmatched/bankUnmatched here — everywhere downstream (tab counts,
  // the "ต้องตรวจสอบ" stat, save/export) sees the reduced "still actually
  // unmatched" lists, not the raw automatic-matching output.
  const matchedSapIdx = useMemo(() => new Set(manualMatches.flatMap((g) => g.sapRows.map((r) => r.sapIdx))), [manualMatches]);
  const matchedBankIdx = useMemo(() => new Set(manualMatches.flatMap((g) => g.bankRows.map((r) => r.bankIdx))), [manualMatches]);
  const payload = useMemo(() => {
    if (!result) return null;
    const base = buildResultPayload(result);
    return {
      ...base,
      sapUnmatched: base.sapUnmatched.filter((r) => !matchedSapIdx.has(r.sapIdx)),
      bankUnmatched: base.bankUnmatched.filter((r) => !matchedBankIdx.has(r.bankIdx)),
      manualMatched: manualMatches,
    };
  }, [result, matchedSapIdx, matchedBankIdx, manualMatches]);
  const dailySummary = useMemo(
    () => (result ? buildDailySummary(result.sapRows, result.bankRows) : []),
    [result]
  );

  const fromMD = isoToMD(dateFrom);
  const toMD = isoToMD(dateTo);
  const hasDateFilter = fromMD != null || toMD != null;
  // Date filter narrows the detail tabs only — the summary card above always
  // shows the full-period totals (also what gets saved), so the filter is
  // purely a browsing aid for inspecting a specific day/range.
  const filteredPayload = useMemo(() => {
    if (!payload) return null;
    if (!hasDateFilter) return payload;
    return {
      matched: payload.matched.filter((r) => inDateRange(r.dateSap, fromMD, toMD)),
      sapUnmatched: payload.sapUnmatched.filter((r) => inDateRange(r.date, fromMD, toMD)),
      bankUnmatched: payload.bankUnmatched.filter((r) => inDateRange(r.date, fromMD, toMD)),
      dateGap: payload.dateGap.filter((r) => inDateRange(r.dateSap, fromMD, toMD)),
    };
  }, [payload, hasDateFilter, fromMD, toMD]);
  const filteredDailySummary = useMemo(() => {
    if (!hasDateFilter) return dailySummary;
    return dailySummary.filter((r) => inDateRange(r.date, fromMD, toMD));
  }, [dailySummary, hasDateFilter, fromMD, toMD]);

  const handleProcess = useCallback(async () => {
    setStatus("processing");
    setError("");
    try {
      const readAoa = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              // cellDates is intentionally OFF: converting Excel date serials to JS
              // Date objects routes through the browser's local timezone, which is
              // unreliable for dates before 1970 (some source files store a garbled
              // year, e.g. "69" meant as พ.ศ. 2569 gets read as ค.ศ. 1969 by Excel's
              // 2-digit-year rule). Reading the raw serial and parsing day/month with
              // pure arithmetic (see parseAnyDate's SSF.parse_date_code branch) avoids
              // that entirely — we only ever use day/month anyway, never the year.
              const wb = readXLSX(e.target.result, { type: "array" });
              const ws = wb.Sheets[wb.SheetNames[0]];
              const aoa = xlsxUtils.sheet_to_json(ws, { header: 1, raw: true, defval: null });
              resolve(aoa);
            } catch (err) {
              reject(err);
            }
          };
          reader.onerror = () => reject(new Error("อ่านไฟล์ไม่สำเร็จ"));
          reader.readAsArrayBuffer(file);
        });

      const [sapAoa, bankAoa] = await Promise.all([readAoa(sapFile), readAoa(bankFile)]);
      const sapRows = parseSapSheet(sapAoa);
      const bankRows = parseBankSheet(bankAoa);

      if (sapRows.length === 0 || bankRows.length === 0) {
        throw new Error(
          "อ่านข้อมูลจากไฟล์ไม่ได้ตามที่คาดไว้ — ตรวจสอบว่าไฟล์เป็นรูปแบบ SAP/Bank Statement เดิมหรือไม่"
        );
      }

      const { confirmed, dateGap, sapUnmatched, bankUnmatched } = reconcile(sapRows, bankRows);

      setResult({ sapRows, bankRows, confirmed, dateGap, sapUnmatched, bankUnmatched });
      setStatus("done");
      setTab("matched");
      setSavedId(null);
      setDateFrom("");
      setDateTo("");
      setManualMatches([]);
      setSelectedSap(new Set());
      setSelectedBank(new Set());
      setSuggestions(null);
      setDismissedSuggestions(new Set());
    } catch (err) {
      setError(err.message || "เกิดข้อผิดพลาดระหว่างประมวลผล");
      setStatus("error");
    }
  }, [sapFile, bankFile]);

  const handleReset = () => {
    setSapFile(null);
    setBankFile(null);
    setResult(null);
    setStatus("idle");
    setError("");
    setPeriod("");
    setSavedId(null);
    setDateFrom("");
    setDateTo("");
    setManualMatches([]);
    setSelectedSap(new Set());
    setSelectedBank(new Set());
    setSuggestions(null);
    setDismissedSuggestions(new Set());
  };

  const handleSave = useCallback(async () => {
    if (!result || !payload) return;
    if (!period.trim()) {
      addToast("error", "กรุณาระบุช่วงเวลา/เดือนของรายงานก่อนบันทึก");
      return;
    }
    setSaving(true);
    try {
      const sapSumNow = round2(result.sapRows.reduce((a, r) => a + r.amount, 0));
      const bankSumNow = round2(result.bankRows.reduce((a, r) => a + r.amount, 0));
      const res = await saveReconciliation({
        period: period.trim(),
        sap_filename: sapFile?.name || "",
        bank_filename: bankFile?.name || "",
        sap_count: result.sapRows.length,
        bank_count: result.bankRows.length,
        matched_count: result.confirmed.length,
        // Post-manual-match counts (payload, not result) — a row folded
        // into a "จับคู่ยอดต่าง" group is no longer "unmatched" as far as
        // the saved report/history list is concerned.
        sap_unmatched_count: payload.sapUnmatched.length,
        bank_unmatched_count: payload.bankUnmatched.length,
        date_gap_count: result.dateGap.length,
        sap_total: sapSumNow,
        bank_total: bankSumNow,
        diff_total: round2(Math.abs(sapSumNow - bankSumNow)),
        payload,
      });
      setSavedId(res.data.id);
      addToast("success", "บันทึกรายงานลงระบบสำเร็จ");
    } catch (err) {
      const msg = err.response?.data?.detail || "บันทึกรายงานไม่สำเร็จ";
      addToast("error", msg);
    } finally {
      setSaving(false);
    }
  }, [result, payload, period, sapFile, bankFile, addToast]);

  const sapSum = result ? round2(result.sapRows.reduce((a, r) => a + r.amount, 0)) : 0;
  const bankSum = result ? round2(result.bankRows.reduce((a, r) => a + r.amount, 0)) : 0;

  const tabs = result && filteredPayload
    ? [
        { key: "matched", label: "จับคู่ได้", count: filteredPayload.matched.length, tone: "green" },
        { key: "sapUn", label: "SAP หาคู่ไม่เจอ", count: filteredPayload.sapUnmatched.length, tone: "red" },
        { key: "bankUn", label: "Bank หาคู่ไม่เจอ", count: filteredPayload.bankUnmatched.length, tone: "red" },
        // Not affected by the date-range filter (unlike the other tabs) —
        // a confirmed group can bundle rows from different dates (that's
        // often the whole point), so there's no single date to filter it by.
        { key: "manual", label: "จับคู่ยอดต่าง", count: manualMatches.length, tone: "purple" },
        { key: "gap", label: "วันที่ไม่ตรงกัน", count: filteredPayload.dateGap.length, tone: "amber" },
        { key: "daily", label: "สรุปรายวัน", count: filteredDailySummary.length, tone: "navy" },
      ]
    : [];

  const toggleSapSelect = (idx) => setSelectedSap((prev) => {
    const next = new Set(prev);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    return next;
  });
  const toggleBankSelect = (idx) => setSelectedBank((prev) => {
    const next = new Set(prev);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    return next;
  });
  const toggleAllSap = (ids, checked) => setSelectedSap((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
    return next;
  });
  const toggleAllBank = (ids, checked) => setSelectedBank((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
    return next;
  });

  const selectedSapRows = payload ? payload.sapUnmatched.filter((r) => selectedSap.has(r.sapIdx)) : [];
  const selectedBankRows = payload ? payload.bankUnmatched.filter((r) => selectedBank.has(r.bankIdx)) : [];

  const confirmManualMatch = () => {
    if (!selectedSapRows.length || !selectedBankRows.length) return;
    const sapTotal = round2(selectedSapRows.reduce((s, r) => s + r.amount, 0));
    const bankTotal = round2(selectedBankRows.reduce((s, r) => s + r.amount, 0));
    setManualMatches((prev) => [...prev, {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      sapRows: selectedSapRows,
      bankRows: selectedBankRows,
      sapTotal,
      bankTotal,
      diff: round2(sapTotal - bankTotal),
    }]);
    setSelectedSap(new Set());
    setSelectedBank(new Set());
  };
  const clearSelection = () => { setSelectedSap(new Set()); setSelectedBank(new Set()); };
  const undoManualMatch = (id) => setManualMatches((prev) => prev.filter((g) => g.id !== id));

  // Runs synchronously (it's fast — see suggestManualMatches' own comment
  // on its bounds) but still goes through a spinner state: without the
  // setTimeout, React wouldn't get a chance to paint "กำลังวิเคราะห์..."
  // before the computation blocks the main thread.
  const runSuggestions = () => {
    if (!payload) return;
    setSuggesting(true);
    setTimeout(() => {
      setSuggestions(suggestManualMatches(payload.sapUnmatched, payload.bankUnmatched));
      setDismissedSuggestions(new Set());
      setSuggesting(false);
    }, 30);
  };

  // Re-filters live rather than mutating `suggestions` on accept — a
  // suggestion whose rows got claimed (by accepting a different
  // suggestion, or by a manual selection on the sapUn/bankUn tabs while
  // suggestions were sitting open) just quietly disappears instead of
  // needing to be found-and-removed from three different places.
  const visibleSuggestions = useMemo(() => {
    if (!suggestions) return [];
    return suggestions.filter((s) =>
      !dismissedSuggestions.has(s.id) &&
      !s.sapRows.some((r) => matchedSapIdx.has(r.sapIdx)) &&
      !s.bankRows.some((r) => matchedBankIdx.has(r.bankIdx))
    );
  }, [suggestions, dismissedSuggestions, matchedSapIdx, matchedBankIdx]);

  const acceptSuggestion = (s) => {
    setManualMatches((prev) => [...prev, {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      sapRows: s.sapRows, bankRows: s.bankRows, sapTotal: s.sapTotal, bankTotal: s.bankTotal, diff: s.diff,
    }]);
  };
  const dismissSuggestion = (id) => setDismissedSuggestions((prev) => new Set(prev).add(id));

  return (
    <div
      style={{
        fontFamily: "'Inter', 'Noto Sans Thai', system-ui, sans-serif",
        background: "#F6F4EE",
        minHeight: "100%",
        padding: "28px 20px 60px",
        color: "#2A3441",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600;700&display=swap');
        * { box-sizing: border-box; }
        input:focus { border-color: #132A3A !important; }
      `}</style>

      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 26 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <ScanLine size={18} color="#1F6650" />
            <span style={{ fontSize: 11.5, letterSpacing: "0.08em", color: "#1F6650", fontWeight: 700, textTransform: "uppercase" }}>
              Reconciliation Tool
            </span>
          </div>
          <h1
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: 30,
              fontWeight: 600,
              color: "#132A3A",
              margin: 0,
              lineHeight: 1.25,
            }}
          >
            ตรวจสอบยอด SAP กับ Bank Statement
          </h1>
          <p style={{ fontSize: 13.5, color: "#63697A", marginTop: 6, maxWidth: 560 }}>
            อัปโหลดไฟล์ทั้งสองฉบับ ระบบจะจับคู่รายการด้วย วันที่ + จำนวนเงิน ให้อัตโนมัติ
            ทุกอย่างประมวลผลในเบราว์เซอร์ของคุณ ไม่มีการอัปโหลดไฟล์ขึ้นเซิร์ฟเวอร์ใดๆ
            ลากไฟล์ทั้งสองฉบับมาวางพร้อมกันในช่องใดช่องหนึ่งก็ได้ ระบบจะแยกให้เองจากชื่อไฟล์
          </p>
        </div>

        {/* Upload card */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: 22,
            border: "1px solid #E7E3D8",
            boxShadow: "0 1px 2px rgba(19,42,58,0.04)",
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Dropzone
              label="ไฟล์ SAP Statement"
              sublabel="ลากไฟล์มาวาง (เลือกได้ทีละ 2 ไฟล์) หรือคลิกเพื่อเลือก (.xlsx)"
              file={sapFile}
              onFiles={handleFilesFor("sap")}
              accent="#1F6650"
            />
            <Dropzone
              label="ไฟล์ Bank Statement"
              sublabel="ลากไฟล์มาวาง (เลือกได้ทีละ 2 ไฟล์) หรือคลิกเพื่อเลือก (.xlsx)"
              file={bankFile}
              onFiles={handleFilesFor("bank")}
              accent="#1F6650"
            />
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button
              onClick={handleProcess}
              disabled={!canProcess}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "12px 18px",
                borderRadius: 10,
                border: "none",
                background: canProcess ? "#132A3A" : "#C8CCD4",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: canProcess ? "pointer" : "not-allowed",
                transition: "background .15s",
              }}
            >
              {status === "processing" ? (
                <>
                  <Loader2 size={16} className="spin" style={{ animation: "spin 1s linear infinite" }} />
                  กำลังประมวลผล...
                </>
              ) : (
                <>เปรียบเทียบข้อมูล</>
              )}
            </button>
            {(sapFile || bankFile || result) && (
              <button
                onClick={handleReset}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "12px 16px",
                  borderRadius: 10,
                  border: "1px solid #DADFE6",
                  background: "#fff",
                  color: "#63697A",
                  fontSize: 13.5,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <RotateCcw size={14} /> ล้างข้อมูล
              </button>
            )}
          </div>

          {status === "error" && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                borderRadius: 8,
                background: "#FBEAE8",
                color: "#B3261E",
                fontSize: 13,
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
              }}
            >
              <XCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              {error}
            </div>
          )}
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        {/* Results */}
        {result && (
          <div style={{ marginTop: 22 }}>
            {/* Receipt-style summary */}
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                border: "1px solid #E7E3D8",
                padding: "24px 26px 20px",
                position: "relative",
                boxShadow: "0 1px 2px rgba(19,42,58,0.04)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CheckCircle2 size={18} color="#1F6650" />
                  <span style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 600, color: "#132A3A" }}>
                    ผลการตรวจสอบ
                  </span>
                </div>
                <button
                  onClick={() => exportWorkbook({
                    payload,
                    dailySummary,
                    sapCount: result.sapRows.length,
                    bankCount: result.bankRows.length,
                    sapTotal: sapSum,
                    bankTotal: bankSum,
                    filename: "CSA_Reconciliation.xlsx",
                  })}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1px solid #1F6650",
                    background: "#E7F3EC",
                    color: "#1F6650",
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <Download size={14} /> ดาวน์โหลด Excel
                </button>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                <StatChip value={result.sapRows.length.toLocaleString()} label="รายการ SAP ทั้งหมด" tone="navy" />
                <StatChip value={result.bankRows.length.toLocaleString()} label="รายการ Bank ทั้งหมด" tone="navy" />
                <StatChip value={result.confirmed.length.toLocaleString()} label="จับคู่ได้ตรงกัน" tone="green" />
                <StatChip
                  value={(payload.sapUnmatched.length + payload.bankUnmatched.length).toLocaleString()}
                  label="รายการต้องตรวจสอบ"
                  tone="red"
                />
                {manualMatches.length > 0 && (
                  <StatChip value={manualMatches.length.toLocaleString()} label="จับคู่ยอดต่างแล้ว" tone="purple" />
                )}
              </div>

              <div
                style={{
                  borderTop: "1.5px dashed #DADFE6",
                  paddingTop: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12.5,
                  color: "#63697A",
                }}
              >
                <span>ผลรวม SAP: <b style={{ fontFamily: "'IBM Plex Mono', monospace", color: "#132A3A" }}>฿{thb(sapSum)}</b></span>
                <span>ผลรวม Bank: <b style={{ fontFamily: "'IBM Plex Mono', monospace", color: "#132A3A" }}>฿{thb(bankSum)}</b></span>
                <span>ส่วนต่าง: <b style={{ fontFamily: "'IBM Plex Mono', monospace", color: "#B3261E" }}>฿{thb(Math.abs(sapSum - bankSum))}</b></span>
              </div>

              {/* Save to database */}
              <div
                style={{
                  borderTop: "1.5px dashed #DADFE6",
                  marginTop: 16,
                  paddingTop: 16,
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <input
                  value={period}
                  onChange={(e) => { setPeriod(e.target.value); setSavedId(null); }}
                  placeholder="ระบุช่วงเวลา/เดือน เช่น มิถุนายน 2569 หรือ 2026-06"
                  style={{
                    flex: 1,
                    minWidth: 220,
                    fontSize: 13,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #DADFE6",
                    outline: "none",
                    fontFamily: "inherit",
                  }}
                />
                <button
                  onClick={handleSave}
                  disabled={saving || !!savedId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "10px 16px",
                    borderRadius: 8,
                    border: "none",
                    background: savedId ? "#E7F3EC" : "#132A3A",
                    color: savedId ? "#1F6650" : "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: saving || savedId ? "default" : "pointer",
                    opacity: saving ? 0.7 : 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {saving ? (
                    <><Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> กำลังบันทึก...</>
                  ) : savedId ? (
                    <><CheckCircle2 size={14} /> บันทึกแล้ว</>
                  ) : (
                    <><Save size={14} /> บันทึกรายงานลงระบบ</>
                  )}
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
              {tabs.map((t) => {
                const active = tab === t.key;
                const toneColor = { green: "#1F6650", red: "#B3261E", amber: "#8A5A00", navy: "#132A3A", purple: "#5B2FA6" }[t.tone];
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 14px",
                      borderRadius: 999,
                      border: `1.5px solid ${active ? toneColor : "#E7EAEF"}`,
                      background: active ? toneColor : "#fff",
                      color: active ? "#fff" : "#63697A",
                      fontSize: 12.5,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {t.label}
                    <span
                      style={{
                        background: active ? "rgba(255,255,255,0.25)" : "#F1F2F4",
                        borderRadius: 999,
                        padding: "1px 7px",
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 11,
                      }}
                    >
                      {t.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Date filter — narrows the tabs below only; summary above always
                shows the full period */}
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #E7E3D8",
                padding: "12px 16px",
                marginTop: 18,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 12.5, color: "#63697A", fontWeight: 600 }}>กรองช่วงวันที่:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: 12.5, padding: "7px 10px", borderRadius: 8, border: "1px solid #DADFE6", outline: "none", fontFamily: "inherit" }}
              />
              <span style={{ fontSize: 12.5, color: "#8B93A1" }}>ถึง</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: 12.5, padding: "7px 10px", borderRadius: 8, border: "1px solid #DADFE6", outline: "none", fontFamily: "inherit" }}
              />
              {hasDateFilter && (
                <button
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  style={{ fontSize: 12.5, color: "#B3261E", background: "transparent", border: "none", cursor: "pointer", fontWeight: 600 }}
                >
                  ล้างตัวกรอง
                </button>
              )}
              <span style={{ fontSize: 11.5, color: "#8B93A1", marginLeft: "auto" }}>
                ดูวันที่เท่านั้น ไม่สนใจปี — ใช้กรองตารางด้านล่าง
              </span>
            </div>

            {/* Table panel */}
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                border: "1px solid #E7E3D8",
                padding: 18,
                marginTop: 12,
              }}
            >
              {(tab === "sapUn" || tab === "bankUn") && (
                <ManualMatchTray
                  selectedSapRows={selectedSapRows}
                  selectedBankRows={selectedBankRows}
                  onConfirm={confirmManualMatch}
                  onClear={clearSelection}
                />
              )}
              {tab === "matched" && (
                <DataTable
                  columns={[
                    { key: "dateSap", label: "วันที่ SAP", date: true },
                    { key: "dateBank", label: "วันที่ Bank", date: true },
                    { key: "amount", label: "จำนวนเงิน", numeric: true },
                    { key: "docno", label: "เลขที่เอกสาร" },
                    { key: "textSap", label: "รายละเอียด SAP" },
                    { key: "detailBank", label: "รายละเอียด Bank" },
                  ]}
                  rows={filteredPayload.matched}
                  emptyText="ไม่มีรายการที่จับคู่ได้"
                  exportFilename="CSA_จับคู่ได้.xlsx"
                />
              )}
              {tab === "sapUn" && (
                <DataTable
                  columns={[
                    { key: "date", label: "วันที่", date: true },
                    { key: "amount", label: "จำนวนเงิน", numeric: true },
                    { key: "docno", label: "เลขที่เอกสาร" },
                    { key: "text", label: "รายละเอียด" },
                  ]}
                  rows={filteredPayload.sapUnmatched}
                  emptyText="ไม่มีรายการค้าง — จับคู่ได้ครบทุกรายการ"
                  exportFilename="CSA_SAP_หาคู่ไม่เจอ.xlsx"
                  selectKey="sapIdx"
                  selectedIds={selectedSap}
                  onToggleRow={toggleSapSelect}
                  onToggleAll={toggleAllSap}
                />
              )}
              {tab === "bankUn" && (
                <DataTable
                  columns={[
                    { key: "date", label: "วันที่", date: true },
                    { key: "amount", label: "จำนวนเงิน", numeric: true },
                    { key: "channel", label: "ช่องทาง" },
                    { key: "detail", label: "รายละเอียด" },
                  ]}
                  rows={filteredPayload.bankUnmatched}
                  emptyText="ไม่มีรายการค้าง — จับคู่ได้ครบทุกรายการ"
                  exportFilename="CSA_Bank_หาคู่ไม่เจอ.xlsx"
                  selectKey="bankIdx"
                  selectedIds={selectedBank}
                  onToggleRow={toggleBankSelect}
                  onToggleAll={toggleAllBank}
                />
              )}
              {tab === "manual" && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                    <button
                      onClick={runSuggestions}
                      disabled={suggesting}
                      style={{
                        display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 8, border: "none",
                        background: suggesting ? "#C8CCD4" : "#5B2FA6", color: "#fff", fontSize: 12.5, fontWeight: 600,
                        cursor: suggesting ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                      }}
                    >
                      {suggesting ? (
                        <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> กำลังวิเคราะห์...</>
                      ) : (
                        <>🔍 วิเคราะห์หาคู่ที่เป็นไปได้</>
                      )}
                    </button>
                    <span style={{ fontSize: 11.5, color: "#8B93A1" }}>
                      เทียบยอดเงิน + วันที่ของ SAP {payload.sapUnmatched.length} รายการ กับ Bank {payload.bankUnmatched.length} รายการที่เหลือ
                      แล้วจัดอันดับว่ารายการไหนมีโอกาสเป็นคู่กัน (สูงสุด 2 รายการต่อฝั่ง)
                    </span>
                  </div>

                  {visibleSuggestions.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#5B2FA6", marginBottom: 8 }}>
                        ข้อเสนอแนะ ({visibleSuggestions.length}) — เรียงจากโอกาสตรงกันมากไปน้อย
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {visibleSuggestions.map((s) => (
                          <SuggestionCard key={s.id} suggestion={s} onAccept={() => acceptSuggestion(s)} onDismiss={() => dismissSuggestion(s.id)} />
                        ))}
                      </div>
                    </div>
                  )}
                  {suggestions !== null && visibleSuggestions.length === 0 && (
                    <div style={{ fontSize: 12.5, color: "#8B93A1", marginBottom: 20 }}>ไม่พบรายการที่น่าจะจับคู่กันได้จากเกณฑ์ที่ตั้งไว้</div>
                  )}

                  <div style={{ fontSize: 12, fontWeight: 700, color: "#132A3A", marginBottom: 8 }}>
                    ยืนยันแล้ว ({manualMatches.length})
                  </div>
                  <ManualMatchPanel groups={manualMatches} onUndo={undoManualMatch} />
                </div>
              )}
              {tab === "gap" && (
                <DataTable
                  columns={[
                    { key: "diff", label: "ส่วนต่าง (วัน)", numeric: true },
                    { key: "dateSap", label: "วันที่ SAP", date: true },
                    { key: "dateBank", label: "วันที่ Bank", date: true },
                    { key: "amount", label: "จำนวนเงิน", numeric: true },
                    { key: "textSap", label: "รายละเอียด SAP" },
                  ]}
                  rows={filteredPayload.dateGap}
                  emptyText="ไม่มีรายการที่วันที่ต่างกัน"
                  exportFilename="CSA_วันที่ไม่ตรงกัน.xlsx"
                />
              )}
              {tab === "daily" && (
                <DataTable
                  columns={[
                    { key: "date", label: "วันที่", date: true },
                    { key: "sapTotal", label: "ยอด SAP", numeric: true },
                    { key: "sapCount", label: "จำนวนรายการ SAP", numeric: true, count: true },
                    { key: "bankTotal", label: "ยอด Bank", numeric: true },
                    { key: "bankCount", label: "จำนวนรายการ Bank", numeric: true, count: true },
                    { key: "diff", label: "ส่วนต่าง", numeric: true },
                  ]}
                  rows={filteredDailySummary}
                  emptyText="ไม่มีข้อมูล"
                  exportFilename="CSA_สรุปรายวัน.xlsx"
                />
              )}
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 16, fontSize: 12, color: "#8B93A1" }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                รายการใน "SAP หาคู่ไม่เจอ" มักรวมรายการบัญชีที่ไม่ใช่รายการเดินบัญชีธนาคารจริง เช่น การตั้งหนี้ (DZ) หรือรายการยกเลิก/เคลียร์บัญชีภายใน (KZ) — ควรตรวจสอบรายละเอียดก่อนสรุปว่าเป็นความผิดพลาด
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
